import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { scoringAPI } from '../lib/api';
import { apiError } from '../components/fleet/fleetUi';
import { useCan } from '../hooks/useCan';
import { ModeBadge } from './ScoringPage';

/**
 * One competition: its screens and PINs, where it runs (Mission Control or a
 * laptop at the venue), its Singular stream, and the results as they stand.
 *
 * Judging happens on the screens, not here. This is the page someone setting
 * up or watching opens: polled every 3 s, so a venue laptop's pushes and the
 * tables show up without a reload.
 */
const POLL_MS = 3000;
const PHASE_TEXT = {
  SETUP: 'Not started', READY: 'Ready for the next boulder', BOULDER: 'Boulder running', CHANGEOVER: 'Changeover',
  PROVISIONAL: 'All boulders complete, results provisional', OFFICIAL: 'Results official',
};

const randomPin = () => String(Math.floor(100000 + Math.random() * 900000));

export default function ScoringCompetitionPage() {
  const { competitionId: id } = useParams();
  const navigate = useNavigate();
  const canEdit = useCan('production:edit');
  const [data, setData] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [linkCode, setLinkCode] = useState(null);
  const [confirm, setConfirm] = useState(null); // 'venue' | 'cloud' | 'delete'
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => Promise.all([scoringAPI.get(id), scoringAPI.results(id)])
    .then(([d, r]) => { setData(d.data); setResults(r.data); setError(''); })
    .catch((err) => setError(apiError(err, 'Could not load the competition'))), [id]);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const act = async (fn, after) => {
    setBusy(true);
    try {
      const res = await fn();
      after?.(res);
      await load();
    } catch (err) {
      setError(apiError(err, 'That did not work'));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  if (!data) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/scoring" className="text-xs text-blue-500 hover:text-blue-400">← Scoring</Link>
        <div className="text-center py-16 text-gray-600">{error || 'Loading…'}</div>
      </div>
    );
  }

  const { competition: c, summary } = data;
  const origin = window.location.origin;
  const screens = [
    ['judge', 'Boulder Controller', 'The judge at the wall, with the tag button', c.judge_pin],
    ['head', 'Head judge', 'Control, amendments, setup and results', c.head_pin],
    ['monitor', 'Holding monitor', 'Full screen for the holding area', null],
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      <Link to="/scoring" className="text-xs text-blue-500 hover:text-blue-400">← Scoring</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-app-fg">{c.name}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {summary.scoring?.label} · {summary.format === 'single-turn' ? 'one turn each' : 'personal clock rotation'} · {summary.athletes} athletes · {PHASE_TEXT[summary.phase]}{summary.phase === 'BOULDER' ? ` (boulder ${summary.boulder} of ${summary.boulders}${summary.paused ? ', paused' : ''})` : ''}
          </p>
          {summary.on_wall && <p className="text-sm text-gray-300 mt-1">{summary.on_wall.incoming ? 'Coming on' : 'On the wall'}: {summary.on_wall.name}</p>}
        </div>
        <ModeBadge mode={c.mode} />
      </div>

      {error && <div className="px-3 py-2 rounded text-sm" style={{ color: 'var(--live)', background: 'var(--live-bg)' }}>{error}</div>}

      <Panel title="Screens">
        {c.mode === 'venue' && (
          <p className="text-sm text-amber-300 mb-3">This competition is being run at the venue. These screens show it live, but judging is done on the venue laptop&apos;s screens.</p>
        )}
        <div className="divide-y divide-app-line-chrome">
          {screens.map(([screen, title, sub, pin]) => (
            <div key={screen} className="flex flex-wrap items-center gap-3 py-2.5">
              <div className="flex-1 min-w-[200px]">
                <div className="text-sm font-medium text-app-fg">{title}</div>
                <div className="text-xs text-gray-500">{sub}</div>
              </div>
              {pin && <span className="text-xs text-gray-400">PIN <span className="font-mono text-app-fg text-sm">{pin}</span></span>}
              <Copy text={`${origin}/scoring/${c.id}/${screen}`} label="Copy link" />
              <a href={`/scoring/${c.id}/${screen}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 text-xs rounded font-medium text-white" style={{ background: 'var(--blue)' }}>Open</a>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-3">Anyone signed in here who can edit this workspace opens the judge and head judge screens without a PIN. The monitor needs neither; add <span className="font-mono">?standings=1</span> for a standings strip.</p>
        {canEdit && (
          <button disabled={busy} onClick={() => act(() => scoringAPI.update(c.id, { judge_pin: randomPin(), head_pin: randomPin() }))} className="mt-2 text-xs text-gray-400 hover:text-app-fg underline">
            New PINs
          </button>
        )}
      </Panel>

      <Panel title="Where it runs">
        {c.mode === 'cloud' ? (
          <div className="space-y-2 text-sm text-gray-300">
            <p>In Mission Control. The screens connect here over the internet and the Singular feed goes from here. A deploy of Mission Control interrupts the screens for a few seconds, so don&apos;t deploy during a competition.</p>
            <p className="text-gray-400">For a venue with poor internet, run it from a laptop at the wall instead: the laptop takes the competition down and runs it on the venue network, and pushes everything back here as it goes.</p>
            {canEdit && <button disabled={busy} onClick={() => setConfirm('venue')} className="px-3 py-1.5 text-sm rounded font-medium bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50">Run at the venue</button>}
          </div>
        ) : (
          <div className="space-y-2 text-sm text-gray-300">
            <p>
              On a laptop at the venue. {c.venue_seen_at
                ? <>It last pushed at <span className="text-app-fg">{new Date(c.venue_seen_at).toLocaleTimeString()}</span>{c.venue_info?.laptop ? ` from ${c.venue_info.laptop}` : ''}; {c.venue_info?.events ?? 0} events here.</>
                : 'It hasn\'t connected yet.'}
            </p>
            {linkCode && <LinkCode code={linkCode} />}
            {canEdit && (
              <div className="flex flex-wrap gap-2">
                {!linkCode && <button disabled={busy} onClick={() => act(() => scoringAPI.linkCode(c.id), (res) => setLinkCode(res.data.link_code))} className="px-3 py-1.5 text-sm rounded border border-app-line-chrome text-gray-300 hover:text-app-fg">New link code</button>}
                <button disabled={busy} onClick={() => setConfirm('cloud')} className="px-3 py-1.5 text-sm rounded font-medium text-white disabled:opacity-50" style={{ background: 'var(--blue)' }}>Bring it back to the cloud</button>
              </div>
            )}
          </div>
        )}
      </Panel>

      {results && <Results results={results} timeMatters={summary.scoring?.system === 'bcs' || summary.format === 'rotation'} />}

      <Panel title="Singular and export">
        <Singular c={c} canEdit={canEdit} busy={busy} onSave={(token) => act(() => scoringAPI.update(c.id, { singular_token: token }))} />
        <div className="flex flex-wrap gap-3 mt-3 text-sm">
          <button onClick={async () => {
            const res = await scoringAPI.events(c.id);
            download(`${c.name.replace(/[^\w-]+/g, '-')}-event-log.json`, JSON.stringify(res.data.events, null, 2), 'application/json');
          }} className="underline text-gray-300 hover:text-app-fg">Download the event log</button>
          <span className="text-gray-500">Results as CSV and PDF are on the head judge&apos;s Results tab.</span>
        </div>
      </Panel>

      {canEdit && (
        <div className="pt-2">
          <button onClick={() => setConfirm('delete')} className="text-xs text-red-400 hover:text-red-300 underline">Delete this competition</button>
        </div>
      )}

      {confirm === 'venue' && (
        <Confirm
          title="Run this competition at the venue?"
          text="Mission Control stops taking changes to it, and gives you a link code. Paste that into the venue laptop's Setup: the laptop takes the competition down and runs it. The screens here keep showing it live."
          action="Run at the venue"
          busy={busy}
          onCancel={() => setConfirm(null)}
          onConfirm={() => act(() => scoringAPI.setMode(c.id, 'venue'), (res) => setLinkCode(res.data.link_code))}
        />
      )}
      {confirm === 'cloud' && (
        <Confirm
          title="Bring it back to the cloud?"
          text={`Mission Control takes over from the laptop. The laptop's link stops working, so anything it records from now on doesn't count${c.venue_seen_at ? `; it last pushed at ${new Date(c.venue_seen_at).toLocaleTimeString()}` : ''}. Do this once the laptop has pushed everything.`}
          action="Bring it back"
          busy={busy}
          onCancel={() => setConfirm(null)}
          onConfirm={() => act(() => scoringAPI.setMode(c.id, 'cloud'), () => setLinkCode(null))}
        />
      )}
      {confirm === 'delete' && (
        <Confirm
          title={`Delete ${c.name}?`}
          text="Its screens close and it disappears from Scoring. The venue laptop's link stops working."
          action="Delete"
          danger
          typeToConfirm={c.name}
          busy={busy}
          onCancel={() => setConfirm(null)}
          onConfirm={() => act(() => scoringAPI.remove(c.id), () => navigate('/scoring'))}
        />
      )}
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <section className="rounded-lg border border-app-line-chrome bg-app-well">
      <h2 className="text-sm font-semibold text-app-fg px-4 py-3 border-b border-app-line-chrome">{title}</h2>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Copy({ text, label = 'Copy' }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); }).catch(() => {}); }}
      className="px-3 py-1.5 text-xs rounded border border-app-line-chrome text-gray-300 hover:text-app-fg"
    >
      {done ? 'Copied' : label}
    </button>
  );
}

function LinkCode({ code }) {
  return (
    <div className="rounded border border-amber-600/50 bg-app-canvas p-3 space-y-2">
      <p className="text-sm text-amber-200">The link code. It&apos;s shown only now; it works until you make a new one or bring the competition back.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <code className="flex-1 min-w-0 break-all px-3 py-2 rounded bg-black/30 border border-app-line-chrome font-mono text-[12px] text-app-fg select-all">{code}</code>
        <Copy text={code} />
      </div>
      <ol className="text-sm text-gray-400 list-decimal list-inside space-y-1">
        <li>On the judge laptop, start the venue server: <span className="font-mono text-[13px] text-gray-300">cd local-agent &amp;&amp; npm run bcs</span> (build the screens first with <span className="font-mono text-[13px] text-gray-300">cd frontend &amp;&amp; npm run build</span>).</li>
        <li>Open <span className="font-mono text-[13px] text-gray-300">http://localhost:4600/bcs/head</span> with the head judge PIN it prints, then Setup → Mission Control.</li>
        <li>Paste the code and choose Link. The laptop takes the competition down, with these PINs and the Singular stream.</li>
      </ol>
    </div>
  );
}

function Results({ results, timeMatters }) {
  const [table, setTable] = useState('overall');
  const rows = results[table] || [];
  return (
    <Panel title={`Results · ${results.results_status.toLowerCase()}`}>
      <div className="flex gap-1 mb-3">
        {[['women', 'Women'], ['men', 'Men'], ['overall', 'Overall']].map(([key, label]) => (
          <button key={key} onClick={() => setTable(key)} className={`px-3 py-1 text-xs rounded ${table === key ? 'bg-app-canvas text-app-fg border border-app-line-chrome' : 'text-gray-400 hover:text-app-fg'}`}>{label}</button>
        ))}
      </div>
      {rows.length === 0 ? <p className="text-sm text-gray-500">No athletes yet. Add them in the head judge&apos;s Setup, or ask Claude.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-gray-500 text-xs">
              <tr>
                <th className="text-left py-1 pr-3">#</th><th className="text-left pr-3">Athlete</th>
                {rows[0].per_boulder.map((_, i) => <th key={i} className="text-left pr-3">B{i + 1}</th>)}
                <th className="text-left pr-3">Total</th>{timeMatters && <th className="text-left">Time left</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.bib}-${r.name}`} className="border-t border-app-line-chrome">
                  <td className="py-1.5 pr-3 text-gray-500">{r.rank}</td>
                  <td className="pr-3 text-app-fg">{r.name}{r.withdrawn ? <span className="text-red-400"> {r.withdrawn}</span> : ''}</td>
                  {r.per_boulder.map((p, i) => <td key={i} className="pr-3 text-gray-300 whitespace-nowrap">{p || '-'}</td>)}
                  <td className="pr-3 font-semibold text-app-fg whitespace-nowrap">{r.total_text}</td>
                  {timeMatters && <td className="font-mono text-gray-400">{Math.floor(r.time_left_seconds / 60)}:{String(r.time_left_seconds % 60).padStart(2, '0')}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function Singular({ c, canEdit, busy, onSave }) {
  const [token, setToken] = useState('');
  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-300">
        Live feed: {c.singular_configured ? <>publishing to the data stream ending <span className="font-mono">{c.singular_hint}</span></> : 'no Singular data stream set'}.
        {c.mode === 'venue' ? ' At the venue, the laptop publishes it.' : ''}
      </p>
      {canEdit && (
        <div className="flex flex-col sm:flex-row gap-2">
          <input type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste a data stream's private token" className="flex-1 px-3 py-1.5 rounded bg-app-canvas border border-app-line-chrome text-app-fg text-sm" />
          <button disabled={busy || !token.trim()} onClick={() => { onSave(token.trim()); setToken(''); }} className="px-3 py-1.5 text-sm rounded font-medium text-white disabled:opacity-50" style={{ background: 'var(--blue)' }}>Save</button>
          {c.singular_configured && <button disabled={busy} onClick={() => onSave(null)} className="px-3 py-1.5 text-sm rounded text-gray-400 hover:text-app-fg">Remove</button>}
        </div>
      )}
    </div>
  );
}

function Confirm({ title, text, action, onCancel, onConfirm, busy, danger, typeToConfirm }) {
  const [typed, setTyped] = useState('');
  const ok = !typeToConfirm || typed === typeToConfirm;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-lg border border-app-line-chrome bg-app-well p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-base font-semibold text-app-fg">{title}</h2>
        <p className="text-sm text-gray-400">{text}</p>
        {typeToConfirm && <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={`Type ${typeToConfirm}`} className="w-full px-3 py-2 rounded bg-app-canvas border border-app-line-chrome text-app-fg text-sm" />}
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm rounded text-gray-400 hover:text-app-fg">Cancel</button>
          <button disabled={busy || !ok} onClick={onConfirm} className={`px-4 py-2 text-sm rounded font-medium text-white disabled:opacity-50 ${danger ? 'bg-red-700 hover:bg-red-600' : ''}`} style={danger ? undefined : { background: 'var(--blue)' }}>{action}</button>
        </div>
      </div>
    </div>
  );
}

function download(filename, text, type) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
